/**
 * Iter90 — Provider healthcheck dashboard.
 * Operator-only view: pings Stripe / OpenRouter / Namecheap / Cloudflare /
 * Resend / MongoDB and surfaces the most recent backend errors so we can
 * diagnose client complaints without digging into supervisor logs.
 *
 * Mounted as the "Salud" tab in /admin.
 */
import React, { useEffect, useState, useCallback } from "react";
import {
  RefreshCw, CheckCircle2, XCircle, AlertTriangle, Bot, Database,
  CreditCard, Globe, Cloud, Mail, ShieldAlert,
} from "lucide-react";
import { authFetch, API } from "../utils/api";

const PROVIDER_META = {
  mongodb:    { label: "MongoDB",        Icon: Database,    accent: "emerald" },
  stripe:     { label: "Stripe",         Icon: CreditCard,  accent: "violet" },
  openrouter: { label: "OpenRouter (IA)", Icon: Bot,         accent: "fuchsia" },
  namecheap:  { label: "Namecheap",      Icon: Globe,       accent: "sky" },
  cloudflare: { label: "Cloudflare",     Icon: Cloud,       accent: "amber" },
  resend:     { label: "Resend (email)", Icon: Mail,        accent: "rose" },
};

const STATUS_STYLE = {
  up:           { bg: "bg-emerald-500/10",  border: "border-emerald-500/30", text: "text-emerald-300",  Icon: CheckCircle2,   label: "OPERATIVO" },
  slow:         { bg: "bg-amber-500/10",    border: "border-amber-500/30",   text: "text-amber-300",    Icon: AlertTriangle,  label: "DEGRADADO" },
  unconfigured: { bg: "bg-gray-500/10",     border: "border-gray-500/30",    text: "text-gray-400",     Icon: ShieldAlert,    label: "SIN CONFIG" },
  down:         { bg: "bg-red-500/10",      border: "border-red-500/30",     text: "text-red-300",      Icon: XCircle,        label: "CAÍDO" },
};

const OVERALL_BADGE = {
  up:       { text: "Todo operativo",       cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" },
  partial:  { text: "Operativo (parcial)",  cls: "bg-blue-500/15 text-blue-300 border-blue-500/30" },
  degraded: { text: "Degradado",            cls: "bg-amber-500/15 text-amber-300 border-amber-500/30" },
  down:     { text: "Hay un proveedor caído", cls: "bg-red-500/15 text-red-300 border-red-500/30" },
};

function ProviderCard({ id, data }) {
  const meta = PROVIDER_META[id] || { label: id, Icon: Cloud, accent: "gray" };
  const style = STATUS_STYLE[data.status] || STATUS_STYLE.down;
  const Icon = meta.Icon;
  const StatusIcon = style.Icon;
  return (
    <div
      className={`relative p-4 rounded-2xl border ${style.bg} ${style.border}`}
      data-testid={`health-provider-${id}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-xl bg-white/[0.06] border border-white/10 flex items-center justify-center">
            <Icon size={16} className="text-white" />
          </div>
          <div>
            <p className="text-xs font-black text-white uppercase tracking-wide leading-tight">{meta.label}</p>
            <p className="text-[9.5px] text-gray-500 font-mono">{id}</p>
          </div>
        </div>
        <div className={`flex items-center gap-1 ${style.text}`}>
          <StatusIcon size={11} />
          <span className="text-[10px] font-black uppercase tracking-wider">{style.label}</span>
        </div>
      </div>
      <p className="text-xs text-gray-300 leading-snug break-words" data-testid={`health-provider-${id}-msg`}>
        {data.message || "—"}
      </p>
      {/* OpenRouter balance bar — extra visual cue when credits are low */}
      {id === "openrouter" && data.credits_total_usd > 0 && (() => {
        const pct = Math.max(0, Math.min(100, (data.credits_remaining_usd / data.credits_total_usd) * 100));
        const low = data.credits_remaining_usd < 10;
        return (
          <div className="mt-3" data-testid="health-openrouter-balance">
            <div className="flex items-center justify-between text-[10px] font-bold mb-1">
              <span className="text-gray-400">Saldo IA</span>
              <span className={low ? "text-red-300" : "text-gray-300"}>
                ${data.credits_remaining_usd.toFixed(2)} / ${data.credits_total_usd.toFixed(2)}
              </span>
            </div>
            <div className="h-1.5 bg-white/[0.06] rounded-full overflow-hidden">
              <div
                className={low ? "bg-red-500 h-full" : "bg-fuchsia-400 h-full"}
                style={{ width: `${pct}%` }}
              />
            </div>
            {low && (
              <p className="text-[10px] text-red-300 font-bold mt-1.5 flex items-center gap-1">
                <AlertTriangle size={10} /> Saldo bajo — recargá en openrouter.ai
              </p>
            )}
          </div>
        );
      })()}
    </div>
  );
}

export default function HealthSection() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await authFetch(`${API}/admin/health`);
      if (!r.ok) {
        setError(r.status === 401 ? "Necesitás re-loguearte como admin." : `Error ${r.status}`);
        return;
      }
      const d = await r.json();
      setData(d);
    } catch (e) {
      setError(e.message || "Error de red");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh every 60s when the tab is active
  useEffect(() => {
    if (!autoRefresh) return;
    const id = setInterval(() => load(), 60000);
    return () => clearInterval(id);
  }, [autoRefresh, load]);

  if (loading && !data) {
    return (
      <div className="px-3 sm:px-6 lg:px-10 py-12 flex items-center justify-center">
        <RefreshCw size={22} className="animate-spin text-gray-500" />
      </div>
    );
  }
  if (error && !data) {
    return (
      <div className="px-3 sm:px-6 lg:px-10 py-12 text-center" data-testid="health-error">
        <ShieldAlert className="mx-auto mb-3 text-red-400" size={32} />
        <p className="text-sm text-red-300 font-bold">{error}</p>
        <button
          onClick={load}
          className="mt-4 px-4 py-2 bg-white/[0.07] border border-white/10 rounded-lg text-xs font-bold text-white hover:bg-white/[0.12]"
          data-testid="health-retry-btn"
        >
          Reintentar
        </button>
      </div>
    );
  }
  if (!data) return null;

  const overall = OVERALL_BADGE[data.overall] || OVERALL_BADGE.down;
  const margin = data.agent_v2_24h?.margin_usd || 0;
  const marginPositive = margin >= 0;

  return (
    <div className="px-3 sm:px-6 lg:px-10 pb-12 space-y-6" data-testid="health-section">
      {/* Header */}
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div>
          <h2 className="text-xl font-black text-white tracking-tight">Salud del sistema</h2>
          <p className="text-xs text-gray-500 mt-0.5">
            Última verificación: {data.checked_at ? new Date(data.checked_at).toLocaleTimeString("es-UY") : "—"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-black uppercase tracking-wider border ${overall.cls}`} data-testid="health-overall-badge">
            {overall.text}
          </span>
          <button
            onClick={() => setAutoRefresh(v => !v)}
            className={`px-2.5 py-1.5 rounded-lg text-[10px] font-bold border transition-colors ${
              autoRefresh ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" : "bg-white/[0.05] text-gray-400 border-white/10"
            }`}
            data-testid="health-auto-refresh-btn"
            title="Actualiza cada 60s"
          >
            Auto · 60s
          </button>
          <button
            onClick={load}
            disabled={loading}
            className="px-2.5 py-1.5 bg-white/[0.07] border border-white/[0.1] rounded-lg text-[10px] font-bold text-gray-300 hover:bg-white/[0.12] inline-flex items-center gap-1"
            data-testid="health-refresh-btn"
          >
            <RefreshCw size={11} className={loading ? "animate-spin" : ""} />
            Actualizar
          </button>
        </div>
      </div>

      {/* Provider cards */}
      <div>
        <p className="text-[10px] font-black text-gray-500 uppercase tracking-widest mb-3">Proveedores externos</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {Object.entries(data.providers || {}).map(([id, p]) => (
            <ProviderCard key={id} id={id} data={p} />
          ))}
        </div>
      </div>

      {/* Agent v2 spend in last 24h */}
      <div>
        <p className="text-[10px] font-black text-gray-500 uppercase tracking-widest mb-3">Agente IA · últimas 24h</p>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {[
            { label: "Ediciones", value: data.agent_v2_24h?.edits || 0, color: "text-white" },
            { label: "Costo OpenRouter", value: `$${(data.agent_v2_24h?.raw_cost_usd || 0).toFixed(3)}`, color: "text-amber-300" },
            { label: "Cobrado a clientes", value: `$${(data.agent_v2_24h?.billed_usd || 0).toFixed(3)}`, color: "text-emerald-300" },
            {
              label: "Margen",
              value: `${margin >= 0 ? "+" : ""}$${margin.toFixed(3)}`,
              color: marginPositive ? "text-emerald-300" : "text-red-300",
            },
          ].map((kpi, i) => (
            <div
              key={i}
              className="p-4 rounded-2xl bg-white/[0.04] border border-white/[0.06]"
              data-testid={`health-agent-kpi-${i}`}
            >
              <p className="text-[10px] font-black text-gray-500 uppercase tracking-widest mb-1.5">{kpi.label}</p>
              <p className={`text-2xl font-black ${kpi.color}`}>{kpi.value}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Recent errors */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <p className="text-[10px] font-black text-gray-500 uppercase tracking-widest">
            Errores · últimas 24h ({data.errors_24h?.total || 0})
          </p>
          {Object.keys(data.errors_24h?.by_router || {}).length > 0 && (
            <div className="flex gap-1.5 flex-wrap">
              {Object.entries(data.errors_24h.by_router).map(([router, count]) => (
                <span
                  key={router}
                  className="text-[10px] font-bold px-2 py-0.5 rounded-md bg-red-500/15 text-red-300 border border-red-500/30"
                >
                  {router} ×{count}
                </span>
              ))}
            </div>
          )}
        </div>
        {(data.errors_24h?.recent || []).length === 0 ? (
          <div className="p-4 rounded-2xl bg-emerald-500/[0.06] border border-emerald-500/20 text-center" data-testid="health-no-errors">
            <CheckCircle2 className="mx-auto mb-2 text-emerald-400" size={20} />
            <p className="text-[11px] font-bold text-emerald-300">Sin errores registrados en 24h</p>
          </div>
        ) : (
          <div className="space-y-1.5" data-testid="health-error-list">
            {data.errors_24h.recent.map((err, i) => (
              <div
                key={i}
                className="flex items-start gap-2 p-2.5 rounded-lg bg-white/[0.03] border border-white/[0.05] font-mono text-[11px]"
              >
                <span className="text-red-400 font-bold flex-shrink-0">{err.router || "?"}</span>
                <span className="text-gray-300 flex-1 break-all">{err.message || err.error || "—"}</span>
                <span className="text-[10px] text-gray-600 flex-shrink-0">
                  {err.created_at ? new Date(err.created_at).toLocaleTimeString("es-UY") : ""}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
